import { useMutation } from '@tanstack/react-query'
import { type PubNubRef, type FetchMessagesParams, PubnubStatus } from 'v2/types/pubnub'
import type { ChatMessagePayload, FileMessagePayload } from 'v2/types/chunk.external'
import { pubnubLogger } from 'v2/utils/logger'
import { getLocalAuth } from 'v2/utils'
import { useSharedConversationStore } from 'v2/state/conversation'
import { isEmpty } from 'lodash'
import type { FetchMessagesResponse } from 'pubnub'
import { queryClient } from 'v2/configs/rq-setup'
import { useTabStore } from 'v2/state/tab'
import type { AsyncMessagePayload } from 'v2/pages/flow/run-info/types'
import { getSettingsQueryKeys } from '../utils/query-key-management'

interface PubnubMutationsProps {
  pubnubRef: React.MutableRefObject<PubNubRef | null>
}

interface SubscribeParams {
  channels: string[]
  timetoken?: number
}

const RETRY_CONFIG = {
  retry: 3,
  retryDelay: 2000,
}

const handlePubnubError = (error: Error, action: string) => {
  pubnubLogger.error(`Error in ${action}:`, JSON.stringify(error))

  if ('status' in error && typeof error.status === 'object' && error.status !== null) {
    const status = error.status as { statusCode?: number; category?: string }

    if (status.statusCode === 403 && status.category === 'PNAccessDeniedCategory') {
      if (useTabStore.getState().pubnubStatus !== PubnubStatus.CONNECTING) {
        pubnubLogger.warn('PubNub token expired. Invalidating token query.')
        queryClient.invalidateQueries(
          { queryKey: getSettingsQueryKeys().pubnubTokenBase },
          { cancelRefetch: false },
        )
      }
    }
  }
}

const wrapWithErrorHandler = async <T>(action: string, fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    handlePubnubError(error as Error, action)
    throw error
  }
}

export function usePubnubMutations({ pubnubRef }: PubnubMutationsProps) {
  const { setPubnubStatus } = useTabStore.getState()

  const subscribeMutation = useMutation<void, Error, SubscribeParams>({
    mutationFn: ({ channels, timetoken }) =>
      wrapWithErrorHandler(
        'subscribe',
        () => pubnubRef.current?.pubnub.subscribe({ channels, timetoken }) ?? Promise.resolve(),
      ),
    ...RETRY_CONFIG,
  })

  const unsubscribeMutation = useMutation<void, Error, string[]>({
    mutationFn: (channels) =>
      wrapWithErrorHandler(
        'unsubscribe',
        () => pubnubRef.current?.pubnub.unsubscribe({ channels }) ?? Promise.resolve(),
      ),
    ...RETRY_CONFIG,
  })

  const unsubscribeAllMutation = useMutation<void, Error, void>({
    mutationFn: () =>
      wrapWithErrorHandler('unsubscribeAll', async () => {
        const response = await pubnubRef.current?.pubnub.unsubscribeAll()
        setPubnubStatus(PubnubStatus.DISCONNECTED)
        return response
      }),
    ...RETRY_CONFIG,
  })

  const publishMutation = useMutation<
    unknown,
    Error,
    {
      channel: string
      message: ChatMessagePayload | FileMessagePayload | AsyncMessagePayload
    }
  >({
    mutationFn: ({ channel, message }) =>
      wrapWithErrorHandler(
        'publish',
        () => pubnubRef.current?.pubnub.publish({ channel, message }) ?? Promise.resolve(null),
      ),
    ...RETRY_CONFIG,
  })

  const fetchMessagesMutation = useMutation<
    FetchMessagesResponse | undefined,
    Error,
    FetchMessagesParams
  >({
    mutationFn: (params) =>
      wrapWithErrorHandler('fetchMessages', async () => {
        const response = await pubnubRef.current?.pubnub.fetchMessages(params)
        return response
      }),
    ...RETRY_CONFIG,
  })

  const getLastReadTtsMutation = useMutation<unknown, Error, void>({
    mutationFn: () =>
      wrapWithErrorHandler('getLastReadTts', async () => {
        const details = getLocalAuth()
        const tts = useSharedConversationStore.getState().lastReadTimestamp
        if (!details.aom_uuid || !isEmpty(tts)) return null
        const memberships = await pubnubRef.current?.pubnub.objects.getMemberships({
          filter: `channel.id == "aom.${details.aom_uuid}"`,
          include: {
            customFields: true,
          },
        })
        pubnubLogger.log('got last read tts', memberships)
        if (memberships?.data?.[0]?.custom)
          useSharedConversationStore.setState({
            lastReadTimestamp: memberships?.data?.[0]?.custom as Record<string, number>,
          })
        else pubnubLogger.log('no last read tts')
        return memberships
      }),
    ...RETRY_CONFIG,
  })

  const setLastReadTtsMutation = useMutation<unknown, Error, void>({
    mutationFn: () =>
      wrapWithErrorHandler('setLastReadTts', async () => {
        const tts = useSharedConversationStore.getState().lastReadTimestamp
        const details = getLocalAuth()
        if (isEmpty(tts) || !details.aom_uuid) return null
        const memberships = await window.cx.pubnubInstance?.objects.setMemberships({
          channels: [
            {
              id: `aom.${details.aom_uuid}`,
              custom: tts,
            },
          ],
        })
        pubnubLogger.log('setting last read tts', memberships)
        return memberships
      }),
    ...RETRY_CONFIG,
  })

  // Setting the pubnub instance on the window object for easy access
  window.cx.pubnub = {
    publishMutation,
  }

  return {
    subscribeMutation,
    unsubscribeMutation,
    unsubscribeAllMutation,
    publishMutation,
    fetchMessagesMutation,
    getLastReadTtsMutation,
    setLastReadTtsMutation,
  }
}
